import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { JwtHelperService } from '@auth0/angular-jwt';
import { MsalService } from '@azure/msal-angular';
import {
  AccountInfo,
  BrowserAuthError,
  InteractionRequiredAuthError,
  PopupRequest,
  SilentRequest,
} from '@azure/msal-browser';
import { Client, ResponseType } from '@microsoft/microsoft-graph-client';
import * as MicrosoftGraph from '@microsoft/microsoft-graph-types';
import { BehaviorSubject, catchError, firstValueFrom, from, Observable, switchMap, throwError } from 'rxjs';
import { ADUser } from '../shared/models/franchise/ad_user';
import { SSOAuthenticationResult } from '../shared/models/franchise/sso-authenticationResult';
import { Identifier } from '../shared/models/storage.models';
import { LoggerService } from '../shared/services/logger.service';
import { graphConfig } from './graph.config';
import { MsalConfig } from './msal.config';

export enum SSOLoginStatus {
  Login = 1,
  Logout = 2,
  Cancelled = 3,
}

@Injectable({ providedIn: 'root' })
export class GraphService {
  private readonly logger = inject(LoggerService);

  private graphClient!: Client;
  private authenticated$: BehaviorSubject<SSOLoginStatus>;
  private ad_user?: ADUser;
  private renewIdTokenRequest = {
    scopes: MsalConfig.msGraphScopes,
    account: MsalConfig.account,
  };
  constructor(
    private http: HttpClient,
    private msalService: MsalService,
    private jwtHelper: JwtHelperService
  ) {
    this.authenticated$ = new BehaviorSubject<SSOLoginStatus>(SSOLoginStatus.Logout);
    this.initGraphClient();
  }

  private initGraphClient(): void {
    this.graphClient = Client.init({
      authProvider: async (done) => {
        const token = this.getValidAccessToken();

        if (token) {
          done(null, token);
          return;
        }

        try {
          const account = this.msalService.instance.getActiveAccount() || this.msalService.instance.getAllAccounts()[0];

          if (!account) {
            this.logger.warn('No MSAL account found for silent token renewal.');
            done('No account available', null);
            return;
          }

          const response = await firstValueFrom(
            this.msalService.acquireTokenSilent({
              scopes: MsalConfig.msGraphScopes,
              account,
            })
          );

          localStorage.setItem(Identifier.TYPE_MSAL_ID_TOKEN, response.accessToken);
          done(null, response.accessToken);
        } catch (error) {
          this.logger.error('Silent token renewal failed in Graph client:', error);
          done(error, null);
        }
      },
    });
  }
  private async getUser(): Promise<ADUser> {
    const user = new ADUser();
    const authenticated = this.authenticated$.toPromise().then();
    if (!authenticated) {
      return null!;
    }
    if (!this.graphClient) {
      this.initGraphClient();
    }
    try {
      // Get the user from Graph (GET /me)
      const graphUser: MicrosoftGraph.User = await this.graphClient.api(graphConfig.graphMeEndpoint).get();
      const response = await this.graphClient
        .api(graphConfig.graphPhotoEndpoint)
        .version('beta')
        .responseType(ResponseType.RAW)
        .get();
      const url = window.URL || window.webkitURL;
      const blob = await response.blob();
      user.displayName = graphUser.displayName || '';
      // Prefer the mail property, but fall back to userPrincipalName
      user.email = graphUser.mail || graphUser.userPrincipalName || '';
      user.timeZone = graphUser?.mailboxSettings?.timeZone || '';
      user.id = graphUser.id || '';
      if (blob) {
        user.avatar = url.createObjectURL(blob);
      } else {
        // Use default avatar
        user.avatar = '/assets/no-profile-photo.png';
      }
    } catch (error) {
      throw new Error(`Could not get user, ${JSON.stringify(error, null, 2)}`, { cause: error });
    }
    return user;
  }
  private getAccessToken(): string {
    if (
      localStorage.getItem(Identifier.TYPE_MSAL_ID_TOKEN) !== undefined &&
      localStorage.getItem(Identifier.TYPE_MSAL_ID_TOKEN) != null
    ) {
      return localStorage.getItem(Identifier.TYPE_MSAL_ID_TOKEN) || '';
    }
    return '';
  }
  private fetchAccessTokenByScopeMSAL(request: SilentRequest | PopupRequest): Observable<SSOAuthenticationResult> {
    let accountObject: AccountInfo | null = MsalConfig.account ?? null;

    if (!accountObject) {
      const accounts = this.msalService.instance.getAllAccounts();
      if (accounts.length > 0) {
        accountObject = accounts[0];
        MsalConfig.account = accountObject;
      }
    }

    if (!accountObject) {
      this.logger.warn('[GraphService] No account found — triggering login popup');
      return this.msalService.acquireTokenPopup(request);
    }

    request.account = accountObject ?? undefined;

    return this.msalService.acquireTokenSilent(request).pipe(
      catchError((error) => {
        this.logger.warn('[GraphService] Silent token acquisition failed:', error);

        return this.msalService.acquireTokenPopup(request).pipe(
          catchError((popupError) => {
            this.logger.error('[GraphService] Popup token acquisition also failed:', popupError);
            return throwError(() => popupError);
          })
        );
      })
    );
  }

  public get ssoAuthenticated$() {
    return this.authenticated$;
  }
  public acquireToken(): void {
    if (!MsalConfig.isEnabled) {
      return;
    }
    this.fetchAccessTokenByScopeMSAL(this.renewIdTokenRequest as any)
      .pipe(
        switchMap((token) => {
          localStorage.setItem(Identifier.TYPE_MSAL_ID_TOKEN, token.accessToken);
          return this.fetchAccessTokenByScopeMSAL({
            scopes: MsalConfig.cmApiRequest,
            account: MsalConfig.account as any,
          });
        })
      )
      .pipe(
        switchMap((authRes) => {
          localStorage.setItem(Identifier.TYPE_MSAL_ACC_TOKEN, authRes.accessToken);
          //return this.getMsalClinicMasterToken(authRes.accessToken);
          return from(this.getUser());
        })
      )
      .subscribe({
        next: (adUser: ADUser) => {
          this.ad_user = adUser;
          if (this.ad_user) {
            localStorage.setItem(Identifier.AD_USER, JSON.stringify(this.ad_user));
          }
          this.authenticated$.next(SSOLoginStatus.Login);
        },
        error: (error) => {
          this.logger.error(error);
          if (error instanceof BrowserAuthError) {
            this.authenticated$.next(SSOLoginStatus.Cancelled);
          }

          if (error instanceof InteractionRequiredAuthError) {
            this.msalService.acquireTokenPopup(this.renewIdTokenRequest as any);
          }
        },
      });
  }

  public getValidAccessToken(): string | null {
    const token = localStorage.getItem(Identifier.TYPE_MSAL_ID_TOKEN);
    if (token && !this.jwtHelper.isTokenExpired(token)) {
      return token;
    }

    this.logger.warn('Token is missing or expired');
    return null;
  }
}
